import React, { useState, useEffect, useMemo, useContext, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
    Typography,
    TableCell,
    Button,
    Switch,
    Stack,
    Paper,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    Chip,
    IconButton,
    Box,
    Modal,
    Drawer,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { ViewState } from '@devexpress/dx-react-scheduler'
import { Scheduler, MonthView, Appointments, AppointmentTooltip } from '@devexpress/dx-react-scheduler-material-ui'
import { AxiosWithAuth, changeSelectedEmployee, changeSelectedStore } from '../../Utilities/authenticationService'
import { RECURRING, CONFIRMED, PALO_BLUE, PATHS } from '../../constants'
import SubmitSvg from './SubmitSvg'
import { ContentCopy, ContentPaste, Settings } from '@mui/icons-material'
import moment from 'moment'
import { InputTime, parseTimeBlock, timezoneOffsets, isInvalidScheduleTime } from '../../Utilities/shiftUtils'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import ButtonWithConfirmPrompt from '../ButtonWithConfirmPrompt'
import RecurringShifts from '../RecurringShifts/RecurringShifts'
import { isMobile } from '../../Utilities/isMobile'
import SelectedEmployee from '../SelectedEmployee'
import SubmitShift from './SubmitShift'
import ChatScheduler from '../openai-stuff/partTimerScheduler'

const Calendar = () => {
    const [snackbar, setSnackbarState] = useState({ open: false, message: '', severity: 'info' })
    const notify = React.useCallback(payload => {
        setSnackbarState({ open: true, ...payload })
    }, [])

    const { calendarYearMonth, employmentId } = useParams()
    const history = useHistory()
    const [year, month] = calendarYearMonth.split('-')
    const [shifts, setShifts] = useState([])
    const [recurringDialog, setRecurringDialog] = useState(false)
    const [recurringLoading, setRecurringLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [locked, setLocked] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)  // Race condition protection
    const [showTooltip, setShowTooltip] = useState(true)
    const [copiedDate, setCopiedDate] = useState(null)
    const { isManagerFor, canEditShiftFor, userEmployments, isAdmin } = useContext(EmploymentsContext)
    const [employees, setEmployees] = useState([])
    const employeesRef = useRef(employees)
    employeesRef.current = employees
    const hasAttemptedOpenStoreFallbackRef = useRef(false)
    const [submitFor, setSubmitFor] = useState()
    // Prefer employment row flags so we don't race App's later isManagerFor/canEditShiftFor maps.
    const isManagerView = useMemo(() => {
        if (isAdmin) return true
        const current = userEmployments?.find(e => String(e.id) === String(employmentId))
        if (current?.is_manager || current?.can_edit_shift) return true
        return Boolean(isManagerFor?.[employmentId] || canEditShiftFor?.[employmentId])
    }, [isAdmin, userEmployments, employmentId, isManagerFor, canEditShiftFor])
    const multiStoreSelf = (userEmployments?.length || 0) > 1
    const storeNames = (userEmployments || [])
        .map(e => e.store?.name)
        .filter(Boolean)
        .join('、')
    const showMultiStoreBanner = !isManagerView && multiStoreSelf
    const showManagerMultiStoreHint = isManagerView
    const submitConfirmContent = (() => {
        const base = '提出したあとは変更できません。提出してよろしいですか？'
        if (isManagerView) {
            return (
                <>
                    <Typography paragraph>{base}</Typography>
                    <Typography paragraph sx={{ mb: 0 }}>
                        この従業員が複数店舗に所属している場合、同じ希望シフトが全所属店舗に提出されます。
                    </Typography>
                </>
            )
        }
        if (multiStoreSelf) {
            return (
                <>
                    <Typography paragraph>{base}</Typography>
                    <Typography paragraph sx={{ mb: 0 }}>
                        この希望シフトは所属する全店舗（{storeNames}）に提出されます。
                    </Typography>
                </>
            )
        }
        return base
    })()
    const hasAttemptedFallbackRef = useRef(false)
    const [submitShiftDialog, setSubmitShiftDialog] = useState(false)
    const [selectedDate, setSelectedDate] = useState(null)
    const [store, setStore] = useState({})
    const [showChat, setShowChat] = useState(false)
    const [blockers, setBlockers] = useState([])
    const [showRecurringShiftModal, setShowRecurringShiftModal] = useState(false)
    const [recurringDirty, setRecurringDirty] = useState(false)
    const [recurringLeaveDialogOpen, setRecurringLeaveDialogOpen] = useState(false)

    const requestCloseRecurringModal = () => {
        if (recurringDirty) {
            setRecurringLeaveDialogOpen(true)
        } else {
            setShowRecurringShiftModal(false)
        }
    }

    const confirmLeaveRecurringWithoutSave = () => {
        setRecurringLeaveDialogOpen(false)
        setRecurringDirty(false)
        setShowRecurringShiftModal(false)
    }

    const blockedDays = useMemo(() => {
        const blocked_shifts_days = shifts
            .filter(s => s.status === 'blocked')
            .map(s => moment(s.startDate).format('YYYY-MM-DD'))
        const shift_blocker_days = blockers.map(s => moment(s.startDate).format('YYYY-MM-DD'))
        return blocked_shifts_days.concat(shift_blocker_days)
    }, [shifts])

    const storeTz = store?.time_zone ? timezoneOffsets[store.time_zone] : null

    const fetchShiftsForEmployment = (employmentIdParam, storeForTz) => {
        if (!storeForTz?.time_zone) return Promise.resolve()
        const tzOffset = timezoneOffsets[storeForTz.time_zone]
        return AxiosWithAuth.get('/shifts', { params: { employment_id: employmentIdParam } })
            .then(res => {
                let shiftsData = res.data.shifts
                const blockersData = res.data.shift_blockers
                shiftsData.forEach(e => {
                    const [startDate, endDate] = parseTimeBlock(e.time_block, tzOffset)
                    e.startDate = startDate
                    e.endDate = endDate
                    e.title = getShiftTitle(startDate, endDate)
                })
                blockersData.forEach(e => {
                    const [startDate, endDate] = parseTimeBlock(e.time_block, tzOffset)
                    e.startDate = startDate
                    e.endDate = endDate
                    e.title = getShiftTitle(startDate, endDate)
                    e.status = 'blocked'
                })
                setBlockers(blockersData)
                setShifts(shiftsData)
            })
            .catch(() => {
                setShifts([])
                setBlockers([])
            })
    }

    const getAllShiftsFor = submitFor => {
        if (store?.id && storeTz != null) {
            return fetchShiftsForEmployment(submitFor, store)
        }
        return Promise.resolve()
    }

    const nonSubmittedShifts = useMemo(() => {
        let val = false
        shifts.forEach(shift => {
            if (shift.startDate.getMonth() + 1 == parseInt(month) && shift.status !== 'submitted') {
                val = true
            }
        })
        return val
    }, [shifts])

    const viewRecurring = useMemo(() => {
        let val = false
        shifts.forEach(shift => {
            if (shift.startDate.getMonth() + 1 == parseInt(month) && shift.is_recurring === true) {
                val = true
            }
        })
        return val
    }, [shifts])


    const copy = date => {
        copiedDate ? setCopiedDate(false) : setCopiedDate(date)
    }

    const paste = async date => {
        const newShifts = [...shifts]

        const copiedShifts = newShifts.filter(
            shift =>
                shift.startDate.getDate() === copiedDate.getDate() &&
                shift.startDate.getMonth() === copiedDate.getMonth() &&
                shift.status === 'available'
        )

        const destinationShifts = newShifts.filter(
            shift => shift.startDate.getDate() === date.getDate() && shift.startDate.getMonth() === date.getMonth() && shift.startDate.getFullYear() === date.getFullYear()
        )

        const copiedShiftsWithoutDupes = copiedShifts.filter(copiedShift => {
            // removes shifts with the same time in the day that is pasted to
            return !destinationShifts.find(destinationShift => {
                return (
                    destinationShift.startDate.getHours() === copiedShift.startDate.getHours() &&
                    destinationShift.startDate.getMinutes() === copiedShift.startDate.getMinutes()
                )
            })
        })

        copiedShiftsWithoutDupes.forEach(shift => {
            const newShift = { ...shift }
            newShift.startDate = new Date(date)
            newShift.startDate.setHours(shift.startDate.getHours())
            newShift.startDate.setMinutes(shift.startDate.getMinutes())
            const duration = shift.endDate - shift.startDate
            newShift.endDate = new Date(newShift.startDate.getTime() + duration)
            newShift.is_recurring = false
            AxiosWithAuth.post('/shifts', {
                employment_id: submitFor,
                start_time: newShift.startDate,
                end_time: newShift.endDate,
                status: 'available',
                submitted_by: employmentId
            })
                .then(res => {
                    newShift.id = res.data.id
                    newShifts.push(newShift)
                    setShifts(newShifts)
                })
                .catch(err => {
                    console.log(err)
                    if (err.response.data.base) {
                        notify({ message: err.response.data.base[0], severity: 'error' })
                    }
                })
        })
    }

    const editsDisabled = locked || recurringLoading

    const CustomCell = ({ startDate, otherMonth, today }) => {
        const isBlocked = blockedDays.includes(moment(startDate).format('YYYY-MM-DD'))
        const isCopied = copiedDate && copiedDate.getDate() === startDate.getDate()
        return (
            <TableCell
                data-testid={moment(startDate).format('YYYY-MM-DD')}
                className="custom-cell"
                style={{
                    height: '120px',
                    borderLeft: '1px solid rgb(224 224 224)',
                    textAlign: isMobile() ? 'left' : 'center',
                    padding: isMobile() ? '5px' : '',
                    verticalAlign: 'top',
                    position: 'relative'
                }}
                onClick={() => {
                    if (!editsDisabled && startDate.getMonth() + 1 === parseInt(month)) {
                        setSubmitShiftDialog(true)
                        setSelectedDate(startDate)
                    }
                }}
            >
                {!editsDisabled && !otherMonth && isCopied ? (
                    <IconButton
                        style={{
                            position: 'absolute',
                            top: isMobile() ? '0px' : '5px',
                            right: isMobile() ? '0px' : '10px'
                        }}
                        onClick={e => {
                            e.stopPropagation()
                            copy(startDate)
                        }}
                    >
                        <ContentCopy className="copy-button" fontSize="small" sx={{ color: PALO_BLUE }} />
                    </IconButton>
                ) : !editsDisabled && !otherMonth && copiedDate ? (
                    <IconButton
                        style={{
                            position: 'absolute',
                            top: isMobile() ? '0px' : '5px',
                            right: isMobile() ? '0px' : '10px'
                        }}
                        onClick={e => {
                            e.stopPropagation()
                            paste(startDate)
                        }}
                    >
                        <ContentPaste fontSize="small" />
                    </IconButton>
                ) : (
                    shifts.find(
                        shift =>
                            shift.startDate.getDate() === startDate.getDate() &&
                            shift.startDate.getMonth() === startDate.getMonth()
                    ) &&
                    !editsDisabled &&
                    !otherMonth &&
                    !isBlocked && (
                        <IconButton
                            style={{
                                position: 'absolute',
                                top: isMobile() ? '0px' : '5px',
                                right: isMobile() ? '0px' : '10px'
                            }}
                            onClick={e => {
                                e.stopPropagation()
                                copy(startDate)
                            }}
                        >
                            <ContentCopy className="copy-button" fontSize="small" />
                        </IconButton>
                    )
                )}
                {today ? (
                    <Chip color="primary" sx={{ fontSize: '1rem' }} label={startDate.getDate()}></Chip>
                ) : (
                    <Typography color={otherMonth ? 'rgba(183, 183, 183, 1)' : 'black'} className="custom-cell-text">
                        {startDate.getDate()}
                    </Typography>
                )}
            </TableCell>
        )
    }

    const CustomHeader = ({ startDate }) => {
        return (
            <TableCell>
                <Typography variant="title" sx={{ textAlign: 'center' }}>
                    {Intl.DateTimeFormat('ja', { weekday: 'short' }).format(startDate)}
                </Typography>
            </TableCell>
        )
    }

    const handleShiftDelete = appointmentData => {
        if (appointmentData.optimistic || recurringLoading) return
        AxiosWithAuth.delete(`/shifts/${appointmentData.id}`)
            .then(() => {
                setShowTooltip(false)
                setShifts(shifts.filter(shift => shift.id !== appointmentData.id))
                setTimeout(() => setShowTooltip(true), 100)
            })
            .catch(err => {
                console.log(err)
            })
    }

    const handleShiftBlockerDelete = appointmentData => {
        if (recurringLoading) return
        AxiosWithAuth.delete(`/shift_blockers/${appointmentData.id}`)
            .then(() => {
                setShowTooltip(false)
                setBlockers(blockers.filter(shift => shift.id !== appointmentData.id))
                setTimeout(() => setShowTooltip(true), 100)
            })
            .catch(err => {
                console.log(err)
            })
    }

    const handleShiftEdit = async shift => {
        if (shift.optimistic || recurringLoading) return
        const [startHour, startMinute] = shift.startTime.split(':')
        const [endHour, endMinute] = shift.endTime.split(':')
        const year = shift.startDate.getFullYear()
        const month = shift.startDate.getMonth()
        const day = shift.startDate.getDate()
        const startTimeDate = new Date(year, month, day, startHour, startMinute)
        const startTokyoHourOffset = startTimeDate.getTimezoneOffset() / 60 + timezoneOffsets[store.time_zone]
        const endTimeDate = new Date(year, month, day, endHour, endMinute)
        const endTokyoHourOffset = endTimeDate.getTimezoneOffset() / 60 + timezoneOffsets[store.time_zone]

        if (startTimeDate.getHours() > endTimeDate.getHours()) {
            // night shift
            endTimeDate.setDate(endTimeDate.getDate() + 1)
        }

        startTimeDate.setHours(startTimeDate.getHours() - startTokyoHourOffset)
        endTimeDate.setHours(endTimeDate.getHours() - endTokyoHourOffset)

        await AxiosWithAuth.put('/shifts/' + shift.id, {
            employment_id: submitFor,
            start_time: startTimeDate,
            end_time: endTimeDate,
            status: shift.status,
            submitted_by: employmentId
        }).catch(err => {
            if (err.response?.status === 422 && err.response.data?.time_block) {
                notify({ message: '他店舗でのシフトと重複しています', severity: 'error' })
            }
            if (err.response?.data?.base) {
                notify({ message: err.response.data.base[0], severity: 'error' })
            }
        })

        setShowTooltip(false)
        getAllShiftsFor(submitFor)
        setTimeout(() => setShowTooltip(true), 100)
    }

    const recurringShiftsThisMonth = currentShifts =>
        currentShifts.filter(
            shift => shift.startDate.getMonth() + 1 == parseInt(month) && shift.is_recurring === true
        )

    const paintRecurringMonth = (parsed, { replaceExisting }) => {
        const monthNumber = parseInt(month)
        setShifts(current => {
            const kept = replaceExisting
                ? current.filter(
                      shift => !(shift.startDate.getMonth() + 1 == monthNumber && shift.is_recurring === true)
                  )
                : current
            const painted = parsed.map((item, idx) => ({
                ...item,
                // Temporary client ids only — never send these to the API.
                id: `optimistic-recurring-${item.id ?? idx}`,
                status: 'available',
                is_recurring: true,
                employment_id: submitFor,
                optimistic: true
            }))
            return kept.concat(painted)
        })
    }

    const handleApplyRecurring = async (action = '') => {
        if (locked) {
            notify({
                message: store?.closed_months?.includes(calendarYearMonth)
                    ? '締め切り済みの月は固定シフトを適用できません。'
                    : '提出済みの月は固定シフトを適用できません。',
                severity: 'warning'
            })
            setRecurringDialog(false)
            return
        }
        const replaceExisting = viewRecurring || action === 'update'
        const applyPattern = !viewRecurring || action === 'update'
        // Capture real DB ids before optimistic paint replaces them.
        const shiftsToDelete = replaceExisting
            ? recurringShiftsThisMonth(shifts)
                  .map(shift => shift.id)
                  .filter(id => typeof id === 'number' || (/^\d+$/.test(String(id)) && !String(id).startsWith('optimistic-')))
            : []
        let shiftsToCreate = []

        setRecurringLoading(true)
        try {
            if (applyPattern) {
                const rsRes = await AxiosWithAuth.get('/recurring_shifts', {
                    params: { employment_id: submitFor }
                })
                const parsed = parseRecurring(rsRes.data || [], calendarYearMonth, getShiftTitle)
                if (parsed.length === 0 && action !== 'update') {
                    notify({
                        message:
                            '固定シフトのパターンが登録されていません。「固定シフトの設定」で曜日と時間を保存してから、もう一度お試しください。',
                        severity: 'warning'
                    })
                    setRecurringDialog(false)
                    return
                }
                // Show the new times immediately. The delete-then-refetch used to blank
                // the month until every sister-store write finished.
                paintRecurringMonth(parsed, { replaceExisting })
                shiftsToCreate = parsed.map(({ startDate, endDate }) => {
                    const freshStartDate = new Date(startDate)
                    const freshEndDate = new Date(endDate)
                    const startTokyoHourOffset = freshStartDate.getTimezoneOffset() / 60 + timezoneOffsets[store.time_zone]
                    const endTokyoHourOffset = freshEndDate.getTimezoneOffset() / 60 + timezoneOffsets[store.time_zone]

                    freshStartDate.setHours(freshStartDate.getHours() - startTokyoHourOffset)
                    freshEndDate.setHours(freshEndDate.getHours() - endTokyoHourOffset)

                    return {
                        employment_id: submitFor,
                        start_time: freshStartDate,
                        end_time: freshEndDate,
                        status: 'available',
                        is_recurring: true,
                        submitted_by: employmentId
                    }
                })
            } else if (replaceExisting) {
                paintRecurringMonth([], { replaceExisting: true })
            }

            try {
                if (shiftsToDelete.length > 0) {
                    await AxiosWithAuth.delete('/multishifts', { data: { shift: { ids: shiftsToDelete } } })
                }
                if (shiftsToCreate.length > 0) {
                    await AxiosWithAuth.post('/multishifts', { shifts: shiftsToCreate })
                }
            } finally {
                // Always replace optimistic rows with server state, even if write failed.
                await getAllShiftsFor(submitFor)
            }
        } finally {
            setRecurringLoading(false)
            setRecurringDialog(false)
        }
    }

    const headerButtonStyle = {
        margin: '5px 5px 5px 5px'
    }
    moment.updateLocale('en', {
        weekdaysShort: ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日']
    })

    const TooltipHeader = ({ appointmentData }) => {
        const startDate = appointmentData.startDate
        return (
            <Stack style={{ margin: '1rem' }}>
                <Typography variant="h6" color="text" align="center" margin={2}>
                    {moment(startDate, 'YYYY-MM-DD').format('YYYY年M月D日 ddd')}
                </Typography>
                {appointmentData.status !== 'confirmed' && (
                    <Typography variant="h6" color="text" align="center">
                        シフトの編集
                    </Typography>
                )}
                {appointmentData.status === 'blocked' && (
                    <Typography variant="h7" color="text" align="center">
                        他店舗でシフトが存在するため、このシフトを変更・保存することはできません
                    </Typography>
                )}
            </Stack>
        )
    }

    const FrederickAppointmentContent = ({ appointmentData }) => {
        const [shift, setShift] = useState({
            ...appointmentData,
            startTime: moment(appointmentData.startDate).format('HH:mm'),
            endTime: moment(appointmentData.endDate).format('HH:mm')
        })
        // Derive errors from current values so Save cannot race a useEffect after blur-commit.
        const startError = isInvalidScheduleTime(shift.startTime)
        const endError = isInvalidScheduleTime(shift.endTime)

        const handleKeyPress = ev => {
            if (ev.key === 'Enter') {
                const nextShift = { ...shift, startTime: ev.target.value }
                setShift(nextShift)
                if (!isInvalidScheduleTime(nextShift.startTime) && !isInvalidScheduleTime(nextShift.endTime)) {
                    handleShiftEdit(nextShift)
                }
            }
        }

        return (
            <div>
                <Stack margin={2} spacing={1.5} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <InputTime
                        startValue={shift.startTime}
                        endValue={shift.endTime}
                        disabled={
                            editsDisabled ||
                            shift.optimistic ||
                            shift.status === 'blocked' ||
                            shift.status === 'confirmed'
                        }
                        startOnChange={e => setShift({ ...shift, startTime: e.target.value })}
                        endOnChange={e => setShift({ ...shift, endTime: e.target.value })}
                        sx={{ marginTop: '.5rem' }}
                        onKeyPress={handleKeyPress}
                        startError={startError}
                        endError={endError}
                    />
                </Stack>
                <Stack direction="row" justifyContent="center" margin={2} spacing={4}>
                    <ButtonWithConfirmPrompt
                        component={
                            <Button
                                variant="outlined"
                                style={headerButtonStyle}
                                startIcon={<DeleteIcon />}
                                disabled={editsDisabled || shift.optimistic || shift.status === 'confirmed'}
                                color="error"
                            >
                                削除
                            </Button>
                        }
                        title="シフトの削除"
                        content="削除してもよろしいですか？"
                        confirmText="削除する"
                        handleConfirm={() => {
                            shift.status === 'blocked' ? handleShiftBlockerDelete(shift) : handleShiftDelete(shift)
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        style={headerButtonStyle}
                        disabled={
                            editsDisabled ||
                            shift.optimistic ||
                            startError ||
                            endError ||
                            shift.status === 'blocked' ||
                            shift.status === 'confirmed'
                        }
                        onClick={() => {
                            if (isInvalidScheduleTime(shift.startTime) || isInvalidScheduleTime(shift.endTime)) {
                                return
                            }
                            handleShiftEdit(shift)
                        }}
                    >
                        保存
                    </Button>
                </Stack>
            </div>
        )
    }

    const customAppointment = ({ style, children, data, ...rest }) => {
        if (data.status === 'confirmed' && !data.is_released && !isManagerView) return null
        return (
            <Appointments.Appointment
                data={data}
                style={{
                    ...style,
                    backgroundColor:
                        data?.status === 'blocked'
                            ? '#ADAEAD'
                            : data?.is_recurring === true && data?.status === 'available'
                            ? RECURRING
                            : data.status === 'confirmed'
                            ? CONFIRMED
                            : data.status === 'available'
                            ? '#64b5f6' // this needs to be dots
                            : data.submitted_by && data.submitted_by !== data.employment_id
                            ? '#90EE90'
                            : '#b6bce2'
                }}
                {...rest}
            >
                {children}
            </Appointments.Appointment>
        )
    }

    const customAppointmentContent = ({ ...restProps }) => {
        // if (restProps.data.status == 'confirmed' && !restProps.data.is_released) return null
        return (
            <Appointments.AppointmentContent {...restProps}>
                <div className={restProps.container}>
                    <div style={{ whiteSpace: 'normal' }}>{restProps.data?.title}</div>
                </div>
            </Appointments.AppointmentContent>
        )
    }

    const getShiftTitle = (start, end) => {
        const startHours = start.getHours()
        const startMinutes = ('00' + start.getMinutes()).slice(-2)
        const endHours = end.getHours()
        const endMinutes = ('00' + end.getMinutes()).slice(-2)
        return `${startHours}:${startMinutes} ${endHours}:${endMinutes}`
    }

    const handleFinalize = async () => {
        // Prevent multiple simultaneous calls - RACE CONDITION FIX
        if (isSubmitting) {
            return;
        }
        
        setIsSubmitting(true);
        setLocked(true);
        
        try {
            const ids = shifts
                .filter(e => {
                    return (
                        !e.optimistic &&
                        e.status === 'available' &&
                        e.startDate.getMonth() + 1 == parseInt(month)
                    )
                })
                .map(e => {
                    return e.id;
                });

            const shiftRes = await AxiosWithAuth.put('/multishifts', {
                shift: {
                    ids: ids,
                    status: 'submitted'
                }
            });

            if (shiftRes?.status === 200) {
                localStorage.setItem('viewRecurring', false);
                setSubmitted(true);
                notify({ 
                    message: 'シフトが正常に提出されました。', 
                    severity: 'success',
                    open: true 
                });
            }
        } catch (error) {
            console.error('Shift submission error:', error);
            notify({ 
                message: '提出に失敗しました。もう一度お試しください。', 
                severity: 'error',
                open: true 
            });
        } finally {
            setIsSubmitting(false);
            setLocked(false);
        }
    }

    useEffect(() => {
        setSubmitFor(localStorage.getItem('submit_for') || employmentId)
        hasAttemptedFallbackRef.current = false
        hasAttemptedOpenStoreFallbackRef.current = false
    }, [employmentId, calendarYearMonth])

    useEffect(() => {
        setLocked(false)
        if (!submitFor) return
        AxiosWithAuth.get('/employments/' + submitFor)
            .then(res => {
                const storeForSubmit = res.data?.store
                const monthClosed = storeForSubmit?.closed_months?.includes(calendarYearMonth)
                if (monthClosed) {
                    const canEditShiftsForStore = isAdmin || userEmployments?.some(
                        e => (e.store_id ?? e.store?.id) === storeForSubmit?.id && (e.is_manager || e.can_edit_shift)
                    )
                    if (!canEditShiftsForStore) {
                        // Part-timers can't switch stores on the submit tab — route to an open affiliation.
                        if (!isManagerView && userEmployments?.length && !hasAttemptedOpenStoreFallbackRef.current) {
                            const storeOpen = emp => !emp?.store?.closed_months?.includes(calendarYearMonth)
                            const selectedId = parseInt(localStorage.getItem('selected_emp'), 10)
                            const primary =
                                userEmployments.find(e => e.id === selectedId) || userEmployments[0]
                            // Prefer primary when open; otherwise first open store (same as Home/SelectStore).
                            const openEmp =
                                (primary && storeOpen(primary) && primary) ||
                                userEmployments.find(storeOpen)
                            if (openEmp && String(openEmp.id) !== String(employmentId)) {
                                hasAttemptedOpenStoreFallbackRef.current = true
                                changeSelectedStore(openEmp.id)
                                history.replace(
                                    PATHS.shiftManagement
                                        .replace(':employmentId', openEmp.id)
                                        .replace(':calendarYearMonth', calendarYearMonth)
                                        .replace(':tabValue', 'submit')
                                )
                                return
                            }
                        }
                        setLocked(true)
                    }
                }
                if (res.data?.submitted && res.data?.submitted?.find(e => e === calendarYearMonth)) {
                    setLocked(true)
                }
                setStore(storeForSubmit)
                if (storeForSubmit?.id) {
                    fetchShiftsForEmployment(submitFor, res.data.store)
                }
            })
            .catch(err => {
                // Employment no longer exists (e.g. archived and rehired with new ID)
                if ((err.response?.status === 404 || err.response?.status === 422) && !hasAttemptedFallbackRef.current) {
                    hasAttemptedFallbackRef.current = true
                    const latest = employeesRef.current
                    const fallbackId = latest.length > 0 ? latest[0].id : employmentId
                    if (fallbackId !== submitFor) {
                        changeSelectedEmployee(fallbackId)
                        setSubmitFor(fallbackId)
                    }
                }
            })
    }, [calendarYearMonth, submitFor, employmentId, userEmployments, isAdmin, isManagerView, history])

    useEffect(() => {
        if (
            employees.length > 0 &&
            employees.find(e => e.id === parseInt(submitFor))?.submitted?.find(e => e === calendarYearMonth)
        ) {
            setLocked(true)
        }
    }, [employees, submitFor, calendarYearMonth])

    useEffect(() => {
        if (localStorage.getItem('rsUpdated')) {
            localStorage.removeItem('rsUpdated')
            if (locked) return
            handleApplyRecurring('update').catch(err => {
                console.error('Failed to update recurring shifts:', err)
                notify({ message: '固定シフトの更新に失敗しました。', severity: 'error' })
            })
        }
    }, [showRecurringShiftModal, locked])

    useEffect(() => {
        if (!isManagerView) return
        AxiosWithAuth.get('/calendars/dailyShifts', {
            params: { employment_id: employmentId, date: calendarYearMonth + '-01' }
        }).then(res => {
            setEmployees(res.data.employments)
        })
    }, [isManagerView, employmentId, calendarYearMonth])

    if (submitted) {
        return (
            <div
                style={{
                    height: 'calc(100vh - 56px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: '0 1rem'
                }}
            >
                <SubmitSvg />
                <Typography variant="header1" sx={{ fontSize: '20px' }}>
                    希望シフトを提出しました。
                </Typography>
                <Button
                    onClick={() => {
                        localStorage.removeItem('viewRecurring')
                        window.location.reload()
                    }}
                    variant="contained"
                    color="paloBlue"
                    fullWidth
                    sx={{ mt: '2rem', mb: '2rem', width: isMobile() ? '100%' : '20%' }}
                >
                    戻る
                </Button>
            </div>
        )
    }
    const ShiftSubmitButton = () => (
        <ButtonWithConfirmPrompt
            component={
                <Button
                    data-tour="shift-submit-button"
                    disabled={!nonSubmittedShifts || editsDisabled || isSubmitting}
                    variant="contained"
                    color="paloBlue"
                    style={{...buttonStyle, margin: isMobile() ? 'auto 1rem 1rem 1rem' : '0 .5rem 0 0', width: isMobile() && '-webkit-fill-available' }}
                >
                    {isSubmitting ? '提出中...' : 'シフトを提出する'}
                </Button>
            }
            title="希望シフトの提出"
            content={submitConfirmContent}
            confirmText={isSubmitting ? "提出中..." : "提出する"}
            handleConfirm={handleFinalize}
            confirmTextDisabled={isSubmitting}
            loading={isSubmitting}
        />
    )
    const buttonStyle = {
        maxHeight: '40px',
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        flexWrap: 'nowrap'
    }

        return (<Box>
            <Snackbar
                open={snackbar.open}
                onClose={() => setSnackbarState(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={4000}
            >
                <Alert
                    severity={snackbar.severity || 'info'}
                    onClose={() => setSnackbarState(s => ({ ...s, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            {isMobile() && <SelectedEmployee employees={employees} setSubmitFor={setSubmitFor} submitFor={submitFor} />}
            {showMultiStoreBanner && (
                <Alert severity="info" sx={{ mx: '1rem', mt: 1 }} data-testid="multi-store-submit-banner">
                    この希望シフトは所属する全店舗に提出されます（{storeNames}）。
                </Alert>
            )}
            {showManagerMultiStoreHint && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mx: '1rem', mt: 1 }}
                    data-testid="manager-multi-store-hint"
                >
                    希望シフトは従業員の全所属店舗に提出されます。店舗選択はこの店舗の従業員一覧を切り替えるためのものです。
                </Typography>
            )}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap'
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ margin: '1rem' }}>
                        <Typography variant="h6">
                            {year}年{parseInt(month)}月
                        </Typography>
                        {locked && store?.closed_months?.includes(calendarYearMonth) && (
                            <Chip label="締め切り済み" color="paloBlue" size="small" />
                        )}
                    </Stack>
                    <Dialog open={recurringDialog}>
                        <DialogTitle>
                            {viewRecurring ? '今月の固定シフトを全て削除します' : '固定シフトを適用します。'}
                        </DialogTitle>
                        <DialogContent>よろしいですか？</DialogContent>
                        <DialogActions>
                            <Button autoFocus onClick={() => setRecurringDialog(false)} disabled={recurringLoading}>
                                キャンセル
                            </Button>
                            <Button 
                                onClick={async () => {
                                    setRecurringLoading(true)
                                    try {
                                        await handleApplyRecurring()
                                    } catch (err) {
                                        console.error('Failed to apply recurring shifts:', err)
                                        notify({ 
                                            message: '固定シフトの適用に失敗しました。もう一度お試しください。', 
                                            severity: 'error' 
                                        })
                                    } finally {
                                        setRecurringLoading(false)
                                    }
                                }} 
                                disabled={recurringLoading}
                            >
                                {recurringLoading ? <CircularProgress size={20} /> : 'OK'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {!isMobile() ? 
                        <Stack direction="row" alignItems="center" sx={{flexWrap: 'wrap'}}>
                            <SelectedEmployee employees={employees} setSubmitFor={setSubmitFor} submitFor={submitFor} />
                            <Typography variant="subtitle1" sx={{...buttonStyle, marginLeft: '1rem'}}>固定シフト</Typography>
                            <Switch
                                checked={viewRecurring}
                                disabled={editsDisabled}
                                onChange={() => {
                                    setRecurringDialog(true)
                                }}
                                color="paloBlue"
                            />
                            <Button
                                onClick={() => setShowRecurringShiftModal(true)}
                                variant="text"
                                color="paloBlue"
                                sx={buttonStyle}
                                startIcon={<Settings />}
                                disabled={editsDisabled}
                            >
                                固定シフトの設定
                            </Button>
                            {ShiftSubmitButton()}
                        </Stack>
                        :
                        <Box display='flex' sx={buttonStyle}>
                            <Button onClick={() => setShowRecurringShiftModal(true)} disabled={editsDisabled}>
                                固定シフトの設定
                            </Button>
                            <Switch
                                checked={viewRecurring}
                                disabled={editsDisabled}
                                onChange={() => {
                                    setRecurringDialog(true)
                                }}
                                color="paloBlue"
                            />
                        </Box>
                    }
                </div>
                <Dialog open={recurringLeaveDialogOpen} onClose={() => setRecurringLeaveDialogOpen(false)}>
                    <DialogTitle>固定シフト</DialogTitle>
                    <DialogContent>
                        変更された固定シフトが保存されていません。このままページを離れると、変更内容は反映されませんが、このままページを離れますか？
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRecurringLeaveDialogOpen(false)} color="primary">
                            キャンセル
                        </Button>
                        <Button onClick={confirmLeaveRecurringWithoutSave} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
                <Modal open={isMobile() && showRecurringShiftModal} onClose={requestCloseRecurringModal}>
                    <Paper sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90vw',
                        p: 2,
                        borderRadius: '10px',
                        height: 'auto',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <RecurringShifts
                            setShowRecurringShiftModal={setShowRecurringShiftModal}
                            onRequestClose={requestCloseRecurringModal}
                            setRecurringDialog={setRecurringDialog}
                            viewRecurring={viewRecurring}
                            submitFor={submitFor}
                            modalOpen={showRecurringShiftModal}
                            onDirtyChange={setRecurringDirty}
                            setSnackbar={notify}
                            monthLocked={locked}
                        />
                    </Paper>
                </Modal>
                <Drawer
                    anchor="right"
                    open={!isMobile() && showRecurringShiftModal}
                    onClose={requestCloseRecurringModal}
                >
                    <div style={{width: '30vw',margin: '1rem'}}>   
                        <RecurringShifts
                            setShowRecurringShiftModal={setShowRecurringShiftModal}
                            onRequestClose={requestCloseRecurringModal}
                            setRecurringDialog={setRecurringDialog}
                            viewRecurring={viewRecurring}
                            submitFor={submitFor}
                            modalOpen={showRecurringShiftModal}
                            onDirtyChange={setRecurringDirty}
                            setSnackbar={notify}
                            monthLocked={locked}
                        />
                    </div>
                </Drawer>

                <Paper sx={{ margin: window.innerWidth > 381 ? '0 1rem' : '1rem 0' }}>
                    <Scheduler
                        data={shifts.concat(blockers)}
                        firstDayOfWeek={1}
                    >
                        <ViewState currentDate={new Date(year, parseInt(month) - 1)} />
                        <MonthView timeTableCellComponent={CustomCell} dayScaleCellComponent={CustomHeader} />
                        <Appointments
                            appointmentComponent={customAppointment}
                            appointmentContentComponent={customAppointmentContent}
                        />
                        {showTooltip && (
                            <AppointmentTooltip
                                headerComponent={TooltipHeader}
                                contentComponent={FrederickAppointmentContent}
                            />
                        )}
                        {submitShiftDialog && (
                            <Dialog open={submitShiftDialog}>
                                <DialogContent>
                                    <SubmitShift
                                        shiftDate={moment(selectedDate).format('YYYY-MM-DD')}
                                        submitFor={submitFor}
                                        setSubmitShiftDialog={setSubmitShiftDialog}
                                        setAllShifts={getAllShiftsFor}
                                        store={store}
                                        setSnackbar={notify}
                                    />
                                </DialogContent>
                            </Dialog>
                        )}
                    </Scheduler>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        {showChat ? (
                            <ChatScheduler
                                getAllShiftsFor={getAllShiftsFor}
                                employmentId={employmentId}
                                year={year}
                                month={month}
                            />
                        ) : (
                            <Button onClick={() => setShowChat(true)}>チャットでシフト入力</Button>
                        )}
                    </div>
                </Paper>
                {isMobile() && ShiftSubmitButton()}
            </Box>
    )
}

export const parseRecurring = (data, calendarYearMonth, getShiftTitle) => {
    const monthStart = moment(calendarYearMonth, 'YYYY-MM').startOf('month')
    const monthEnd = moment(calendarYearMonth, 'YYYY-MM').endOf('month')
    const parsedRecurring = []
    data.forEach(e => {
        const current = monthStart.clone().day(e.day)
        if (current >= monthStart) {
            current.add(-7, 'day')
        }
        while (current.add(7, 'day') <= monthEnd) {
            const clone = current.clone()
            const start_time = clone.toDate()
            start_time.setHours(...e.start_time.split(':'))
            const end_time = clone.toDate()
            end_time.setHours(...e.end_time.split(':'))
            end_time < start_time && end_time.setDate(end_time.getDate() + 1)
            parsedRecurring.push({
                id: start_time.getTime(),
                startDate: start_time,
                endDate: end_time,
                title: getShiftTitle(start_time, end_time),
                status: 'recurring'
            })
        }
    })
    return parsedRecurring
}

export default Calendar
